/**
 * Blog list + detail. Public, indexable. Every post carries SEO tags.
 * Routes: /blog and /blog/:slug
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Eye, ArrowLeft, Share2, Twitter, Linkedin } from "lucide-react";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

function applyBlogSEO(post) {
  if (typeof document === "undefined") return;
  document.title = `${post.title} — WaveScorePro Blog`;
  const canonical = `${window.location.origin}/blog/${post.slug}`;
  const setMeta = (attr, value, content) => {
    let el = document.head.querySelector(`meta[${attr}="${value}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, value);
      el.setAttribute("data-wsp-managed", "1");
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  const setLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement("link");
      el.setAttribute("rel", rel);
      el.setAttribute("data-wsp-managed", "1");
      document.head.appendChild(el);
    }
    el.setAttribute("href", href);
  };
  setMeta("name", "description", post.excerpt || "");
  setMeta("property", "og:type", "article");
  setMeta("property", "og:title", post.title);
  setMeta("property", "og:description", post.excerpt || "");
  setMeta("property", "og:url", canonical);
  setMeta("property", "og:site_name", "WaveScorePro");
  setMeta("property", "article:published_time", post.published_at);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", post.title);
  setMeta("name", "twitter:description", post.excerpt || "");
  setLink("canonical", canonical);
  const prior = document.head.querySelector('script[type="application/ld+json"][data-wsp-managed="1"]');
  if (prior) prior.remove();
  const ld = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "datePublished": post.published_at,
    "dateModified": post.updated_at,
    "url": canonical,
    "author": { "@type": "Organization", "name": "WaveScorePro" },
    "publisher": { "@type": "Organization", "name": "WaveScorePro",
      "url": "https://www.wavescorepro.com" },
  };
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-wsp-managed", "1");
  s.textContent = JSON.stringify(ld);
  document.head.appendChild(s);
}

// Minimal markdown → JSX renderer. We keep it tiny to avoid a heavy dep and
// because the LLM's output shape is constrained (H1/H2/paragraphs/links).
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let para = [];
  const flushPara = () => {
    if (!para.length) return;
    const text = para.join(" ");
    out.push(
      <p key={out.length} style={{ margin: "0 0 16px 0", lineHeight: 1.7, color: "#334155" }}>
        {renderInline(text)}
      </p>
    );
    para = [];
  };
  for (const raw of lines) {
    const line = raw;
    if (line.startsWith("## ")) {
      flushPara();
      out.push(<h2 key={out.length} style={{ fontSize: 22, fontWeight: 800, color: T.navy, margin: "28px 0 12px" }}>{line.slice(3)}</h2>);
    } else if (line.startsWith("### ")) {
      flushPara();
      out.push(<h3 key={out.length} style={{ fontSize: 18, fontWeight: 700, color: T.navy, margin: "20px 0 8px" }}>{line.slice(4)}</h3>);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      flushPara();
      out.push(<li key={out.length} style={{ marginBottom: 6, lineHeight: 1.6 }}>{renderInline(line.slice(2))}</li>);
    } else if (line.trim() === "") {
      flushPara();
    } else {
      para.push(line);
    }
  }
  flushPara();
  return out;
}

function renderInline(text) {
  // [label](url) → <Link>/<a>, **bold** → <strong>, _italic_ → <em>
  const parts = [];
  let i = 0; let key = 0;
  const push = (node) => parts.push(<span key={key++}>{node}</span>);
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let m;
  let last = 0;
  while ((m = linkRe.exec(text))) {
    if (m.index > last) push(text.slice(last, m.index));
    const [_, label, href] = m;
    const isInternal = href.startsWith("/");
    push(isInternal
      ? <Link to={href} style={{ color: T.greenAccent, fontWeight: 600 }}>{label}</Link>
      : <a href={href} target="_blank" rel="noreferrer" style={{ color: T.greenAccent, fontWeight: 600 }}>{label}</a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) push(text.slice(last));
  return parts;
}

export function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    document.title = "Blog — WaveScorePro";
    api.get("/blog?limit=30").then((r) => {
      setPosts(r.data.posts || []);
    }).finally(() => setLoading(false));
    return () => document.title = "WaveScorePro";
  }, []);
  return (
    <div data-testid="blog-list" style={{ minHeight: "100vh", background: T.bg, padding: "40px 24px" }}>
      <div style={{ maxWidth: 800, margin: "0 auto" }}>
        <h1 style={{ fontSize: 40, fontWeight: 800, color: T.navy, margin: "0 0 8px" }}>Blog</h1>
        <p style={{ color: T.muted, fontSize: 15, marginBottom: 32 }}>
          Data-driven analysis of West African radio-ad activity — written from real,
          verifiable broadcast data. Twice a week.
        </p>
        {loading && <div style={{ color: T.muted }}>Loading…</div>}
        {!loading && posts.length === 0 && (
          <div data-testid="blog-empty" style={{ padding: 60, textAlign: "center", color: T.muted,
            background: "#fff", borderRadius: 12, border: `1px solid ${T.border}` }}>
            No posts yet. The first post will publish once we have a week of verified data.
          </div>
        )}
        {posts.map((p, i) => (
          <Link
            key={p.id}
            data-testid={`blog-list-row-${i}`}
            to={`/blog/${p.slug}`}
            style={{ display: "block", padding: 20, background: "#fff",
              border: `1px solid ${T.border}`, borderRadius: 12, marginBottom: 12,
              textDecoration: "none", color: "inherit" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.navy, marginBottom: 6 }}>{p.title}</div>
            <div style={{ fontSize: 13, color: T.muted, marginBottom: 8, display: "flex", gap: 12 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Calendar size={12} /> {(p.published_at || "").slice(0, 10)}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Eye size={12} /> {(p.view_count || 0).toLocaleString()} views
              </span>
            </div>
            <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.6 }}>{p.excerpt}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        // Defensive: an SEO helper defect must never take down the page.
        try { applyBlogSEO(r.data); } catch (e) { console.warn("applyBlogSEO failed:", e); }
      })
      .catch((e) => setError(e.response?.data?.detail || "Post not found"));
    return () => {
      document.title = "WaveScorePro";
      document.head.querySelectorAll('[data-wsp-managed="1"]').forEach((el) => el.remove());
    };
  }, [slug]);
  if (error) return <div style={{ padding: 60, textAlign: "center" }}>{error}</div>;
  if (!post) return <div style={{ padding: 60, textAlign: "center", color: T.muted }}>Loading…</div>;
  const url = typeof window !== "undefined" ? `${window.location.origin}/blog/${slug}` : "";
  return (
    <article data-testid="blog-post" style={{ minHeight: "100vh", background: T.bg, padding: "40px 24px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <Link to="/blog" data-testid="blog-back-link" style={{ display: "inline-flex", alignItems: "center", gap: 6,
          color: T.greenAccent, fontSize: 13, fontWeight: 700, textDecoration: "none", marginBottom: 20 }}>
          <ArrowLeft size={14} /> All posts
        </Link>
        <h1 style={{ fontSize: 40, fontWeight: 800, color: T.navy, lineHeight: 1.15, margin: "0 0 12px" }}>
          {post.title}
        </h1>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 24, display: "flex", gap: 14 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Calendar size={12} /> {(post.published_at || "").slice(0, 10)}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Eye size={12} /> {(post.view_count || 0).toLocaleString()} views
          </span>
        </div>
        <div style={{ background: "#fff", padding: "36px 40px", border: `1px solid ${T.border}`,
          borderRadius: 12, fontSize: 16, color: "#1E293B" }}>
          {renderMarkdown(post.body_markdown)}
        </div>
        <div data-testid="blog-share" style={{ marginTop: 20, padding: 16, background: "#F8FAFC",
          border: `1px solid ${T.border}`, borderRadius: 10,
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.navy, fontSize: 13, fontWeight: 700 }}>
            <Share2 size={16} /> Share this post
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <a data-testid="blog-share-twitter"
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(url)}`}
              target="_blank" rel="noreferrer noopener"
              style={{ display: "inline-flex", alignItems: "center", gap: 6,
                background: "#000", color: "#fff", padding: "8px 14px",
                borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
              <Twitter size={14} /> Post on X
            </a>
            <a data-testid="blog-share-linkedin"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
              target="_blank" rel="noreferrer noopener"
              style={{ display: "inline-flex", alignItems: "center", gap: 6,
                background: "#0A66C2", color: "#fff", padding: "8px 14px",
                borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>
              <Linkedin size={14} /> Share on LinkedIn
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
